import { PropsWithChildren, ReactNode, useMemo } from 'react'

import { pageTitleHeight } from '@/constants'
import { Box, BoxProps, LinearProgress, Skeleton, Stack, Typography } from '@mui/material'
import { getTestId } from '@utils/helpers'
import { kebabCase } from 'lodash-es'

type Props = {
	maxWidth?: string
	description?: string
	ignoreProperCase?: boolean
	refreshing?: boolean
} & (ReactNodeTitle | StringTitle) &
	Omit<BoxProps, 'title' | 'maxWidth'>

type ReactNodeTitle = {
	title: ReactNode
	documentTitle: string // title for the browser tab
}

type StringTitle = {
	title: string
	documentTitle?: string // title for the browser tab
}

/**
 * Display general page title
 */
export default function PageTitle({
	title,
	description = null,
	documentTitle = null,
	refreshing = false,
	children = null,
	maxWidth = null,
	...rest
}: Props & PropsWithChildren) {
	if (typeof title === 'string') {
		document.title = title
	} else {
		document.title = documentTitle
	}

	const testId = useMemo(() => {
		const label = getTestId(rest, document.title)
		return `page-${kebabCase(label)}`
	}, [rest])

	const renderedTitle = useMemo(() => {
		if (typeof title === 'string') {
			return title === '' ? <Skeleton sx={{ width: 200 }} /> : title
		}
		return title
	}, [title])

	return (
		<Box
			sx={{
				height: pageTitleHeight,
				position: 'sticky',
				top: 0,
				backgroundColor: 'background.default',
				zIndex: 10,
				px: '2rem',
				pt: '1rem',
				pb: '0rem',
			}}
			{...rest}
		>
			<Stack
				alignItems="center"
				justifyContent="center"
				sx={{ width: '100%' }}
				data-testid={testId}
			>
				<Stack
					direction="row"
					gap={1}
					sx={{
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						width: '100%',
						maxWidth,
					}}
				>
					<Stack direction="row" gap={1} sx={{ alignItems: 'flex-end', flex: 1 }}>
						<Typography noWrap variant="body1" component="div">
							{renderedTitle}
						</Typography>
						{description && <Typography variant="subtitle2">{description}</Typography>}
					</Stack>
					{children}
				</Stack>
			</Stack>
			{refreshing && (
				<LinearProgress
					color="secondary"
					sx={{ position: 'absolute', bottom: 0, right: 0, left: 0 }}
				/>
			)}
		</Box>
	)
}
